/**
 * Composants accessibles pour LEMO
 * Amélioration ARIA et accessibilité
 */
import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Image accessible avec alt obligatoire
 */
export const AccessibleImage = forwardRef(({
  src,
  alt,
  className,
  decorative = false,
  ...props
}, ref) => {
  return (
    <img
      ref={ref}
      src={src}
      alt={decorative ? '' : alt}
      aria-hidden={decorative}
      loading="lazy"
      className={cn('', className)}
      {...props}
    />
  );
});
AccessibleImage.displayName = 'AccessibleImage';

/**
 * Région live pour les annonces dynamiques
 */
export const LiveRegion = ({ children, priority = 'polite', className }) => {
  return (
    <div
      role="status"
      aria-live={priority}
      aria-atomic="true"
      className={cn('sr-only', className)}
    >
      {children}
    </div>
  );
};

/**
 * Skip link pour la navigation au clavier
 */
export const SkipLink = ({ href = '#main-content', children = 'Aller au contenu principal' }) => {
  return (
    <a
      href={href}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-emerald-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
    >
      {children}
    </a>
  );
};

/**
 * Badge de statut accessible
 */
export const StatusBadge = ({ status, statusMap, className }) => {
  const config = statusMap[status] || { label: status, color: 'gray' };
  
  return (
    <span
      role="status"
      aria-label={`Statut: ${config.label}`}
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        className
      )}
    >
      {config.label}
    </span>
  );
};

/**
 * Loader accessible
 */
export const AccessibleLoader = ({ label = 'Chargement en cours...', size = 'md' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };
  
  return (
    <div role="status" aria-label={label}>
      <div
        className={cn(
          'animate-spin rounded-full border-2 border-gray-300 border-t-emerald-600',
          sizes[size]
        )}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </div>
  );
};

/**
 * Prix accessible
 */
export const AccessiblePrice = ({ amount, currency = '€', className }) => {
  const formattedAmount = typeof amount === 'number' ? amount.toFixed(2) : amount;
  
  return (
    <span
      aria-label={`${formattedAmount} ${currency === '€' ? 'euros' : currency}`}
      className={className}
    >
      {formattedAmount}{currency}
    </span>
  );
};

/**
 * Note étoiles accessible
 */
export const AccessibleRating = ({ rating, maxRating = 5, reviewCount, className }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  return (
    <div 
      role="img" 
      aria-label={`Note: ${rating} sur ${maxRating}${reviewCount ? `, ${reviewCount} avis` : ''}`}
      className={cn('flex items-center gap-1', className)}
    >
      {[...Array(maxRating)].map((_, i) => (
        <svg
          key={i}
          className={cn(
            'w-4 h-4',
            i < fullStars ? 'text-yellow-400 fill-current' :
            i === fullStars && hasHalfStar ? 'text-yellow-400 fill-current opacity-50' :
            'text-gray-300'
          )}
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {reviewCount !== undefined && (
        <span className="text-sm text-gray-500 ml-1">({reviewCount})</span>
      )}
    </div>
  );
};

/**
 * Card accessible avec région
 */
export const AccessibleCard = forwardRef(({ 
  children, 
  as: Component = 'article',
  label,
  className,
  ...props 
}, ref) => {
  return (
    <Component
      ref={ref}
      role="region"
      aria-label={label}
      className={cn('', className)}
      {...props}
    >
      {children}
    </Component>
  );
});
AccessibleCard.displayName = 'AccessibleCard';

/**
 * Liste accessible
 */
export const AccessibleList = ({ 
  items, 
  renderItem, 
  label,
  emptyMessage = 'Aucun élément',
  className 
}) => {
  if (!items || items.length === 0) {
    return (
      <p role="status" className="text-gray-500 text-center py-4">
        {emptyMessage}
      </p>
    );
  }
  
  return (
    <ul 
      role="list" 
      aria-label={label}
      className={cn('', className)}
    >
      {items.map((item, index) => (
        <li key={item.id || index} role="listitem">
          {renderItem(item, index)}
        </li>
      ))}
    </ul>
  );
};

export default {
  AccessibleImage,
  LiveRegion,
  SkipLink,
  StatusBadge,
  AccessibleLoader,
  AccessiblePrice,
  AccessibleRating,
  AccessibleCard,
  AccessibleList
};
